import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAdminVerification } from "../hooks/useAdminVerification";
import { supabase } from "../lib/supabase";
import { Loader2 } from "lucide-react";

type MfaStatus = "checking" | "ok" | "needs-setup" | "needs-challenge";

/**
 * Componente per proteggere le route amministrative.
 *
 * Implementa verifica MULTI-LAYER:
 * 1. Client-side: Controllo sessione locale (veloce)
 * 2. Server-side: Verifica ruolo admin nel database (sicura) - useAdminVerification
 * 3. MFA: Richiede un secondo fattore (TOTP) completato per gli admin (aal2)
 *
 * Questo previene JWT tampering, privilege escalation e l'accesso al
 * pannello con la sola password se l'account admin è compromesso.
 * - Mostra loading mentre verifica il ruolo e il livello MFA
 * - Reindirizza a login se non autenticato
 * - Reindirizza a /admin/mfa-setup se l'admin non ha ancora registrato la 2FA
 * - Reindirizza a /admin/mfa-challenge se la 2FA è registrata ma non ancora completata in questa sessione
 * - Reindirizza a home se non admin
 */
const AdminRoute = (): React.ReactElement | null => {
	const { user, verifying, verified, error: verificationError } = useAdminVerification();
	const location = useLocation();
	const [mfaStatus, setMfaStatus] = useState<MfaStatus>("checking");

	useEffect(() => {
		const checkMfaLevel = async () => {
			if (!verified) {
				return;
			}

			try {
				const { data, error } =
					await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

				if (error) {
					console.error("Error reading MFA assurance level:", error);
					setMfaStatus("needs-challenge");
					return;
				}

				if (data.currentLevel === "aal2") {
					setMfaStatus("ok");
				} else if (data.nextLevel === "aal2") {
					// Un fattore è registrato ma questa sessione non l'ha ancora usato
					setMfaStatus("needs-challenge");
				} else {
					// Nessun fattore MFA registrato per questo account
					setMfaStatus("needs-setup");
				}
			} catch (err) {
				console.error("MFA level check error:", err);
				setMfaStatus("needs-challenge");
			}
		};

		checkMfaLevel();
	}, [verified]);

	// 1. Attendiamo caricamento dell'utente e verifica admin
	if (verifying) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader2 className="h-8 w-8 animate-spin text-primary" />
			</div>
		);
	}

	// 2. Se non loggato, reindirizza a login
	if (!user) {
		return <Navigate to="/auth" state={{ from: location }} replace />;
	}

	// 3. Se verifica admin fallisce, reindirizza a home
	if (!verified) {
		console.warn(
			"Access denied: User failed admin verification",
			verificationError,
		);
		return <Navigate to="/" replace />;
	}

	// 4. Attendiamo il controllo del livello MFA
	if (mfaStatus === "checking") {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader2 className="h-8 w-8 animate-spin text-primary" />
			</div>
		);
	}

	// 5. 2FA obbligatoria per gli admin: registrazione o challenge mancanti
	if (mfaStatus === "needs-setup") {
		return <Navigate to="/admin/mfa-setup" state={{ from: location }} replace />;
	}

	if (mfaStatus === "needs-challenge") {
		return (
			<Navigate to="/admin/mfa-challenge" state={{ from: location }} replace />
		);
	}

	// 6. Se tutto ok (admin verificato + aal2), mostra il contenuto protetto
	return <Outlet />;
};

export default AdminRoute;
